import React, { useState, useEffect } from 'react';
import { ProjectData } from './ProjectData';
import { CVData } from './CVContent';
import { PortfolioData } from '../data/PortfolioData';
import PortfolioVisualEditor from './PortfolioVisualEditor';
import CVVisualEditor from './CVVisualEditor';
import ProjectVisualEditor from './ProjectVisualEditor';

const DataEditor = () => {
  const [projectJson, setProjectJson] = useState('');
  const [cvJson, setCvJson] = useState('');
  const [portfolioJson, setPortfolioJson] = useState('');
  const [activeTab, setActiveTab] = useState('portfolio');
  const [viewMode, setViewMode] = useState('visual'); // 'visual' or 'json'
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    // Format the data nicely with 2 spaces indentation
    setProjectJson(JSON.stringify(ProjectData, null, 2));
    setCvJson(JSON.stringify(CVData, null, 2));
    setPortfolioJson(JSON.stringify(PortfolioData, null, 2));
  }, []);

  const handleCopy = (type) => {
    let textToCopy = '';
    if (type === 'projects') textToCopy = `export const ProjectData =\n${projectJson};`;
    else if (type === 'cv') textToCopy = `export const CVData = ${cvJson};`;
    else if (type === 'portfolio') textToCopy = `export const PortfolioData = ${portfolioJson};`;

    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopyStatus(`Copied ${type} data!`);
      setTimeout(() => setCopyStatus(''), 3000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      setCopyStatus('Failed to copy');
    });
  };

  const handleDownload = (type) => {
    let textToDownload = '';
    let filename = '';
    
    if (type === 'projects') {
      textToDownload = `export const ProjectData =\n${projectJson};`;
      filename = 'ProjectData.js';
    } else if (type === 'cv') {
      textToDownload = `export const CVData = ${cvJson};`;
      filename = 'CVContent.js';
    } else if (type === 'portfolio') {
      textToDownload = `export const PortfolioData = ${portfolioJson};`;
      filename = 'PortfolioData.js';
    }
    
    const blob = new Blob([textToDownload], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Only render if we are on localhost
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-xl">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-300">The data editor is only available in local development mode.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8 pt-24">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Local Data Editor</h1>
          {copyStatus && (
            <span className="bg-green-100 text-green-800 px-4 py-2 rounded-md font-medium transition-opacity duration-300">
              {copyStatus}
            </span>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              className={`flex-1 py-4 px-6 text-center font-medium text-sm transition-colors ${
                activeTab === 'portfolio'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600 dark:bg-gray-700 dark:text-blue-400'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
              onClick={() => setActiveTab('portfolio')}
            >
              PortfolioData.js
            </button>
            <button
              className={`flex-1 py-4 px-6 text-center font-medium text-sm transition-colors ${
                activeTab === 'cv'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600 dark:bg-gray-700 dark:text-blue-400'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
              onClick={() => setActiveTab('cv')}
            >
              CVContent.js
            </button>
            <button
              className={`flex-1 py-4 px-6 text-center font-medium text-sm transition-colors ${
                activeTab === 'projects'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600 dark:bg-gray-700 dark:text-blue-400'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
              onClick={() => setActiveTab('projects')}
            >
              ProjectData.js
            </button>
          </div>

          {/* Editor Area */}
          <div className="p-6">
            <div className="mb-4 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {viewMode === 'visual' 
                    ? "Edit visually, then click 'Apply Changes to JSON' before copying." 
                    : "Edit the JSON below. When finished, copy or download the file."}
                </p>
                
                {/* View Toggle */}
                <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('visual')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      viewMode === 'visual' 
                        ? 'bg-white dark:bg-gray-600 text-gray-800 dark:text-white shadow-sm' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Visual Form
                  </button>
                  <button
                    onClick={() => setViewMode('json')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      viewMode === 'json' 
                        ? 'bg-white dark:bg-gray-600 text-gray-800 dark:text-white shadow-sm' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Raw JSON
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => handleCopy(activeTab)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy Code
                </button>
                <button
                  onClick={() => handleDownload(activeTab)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded-md font-medium transition-colors flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download .js
                </button>
              </div>
            </div>

            {viewMode === 'visual' && activeTab === 'portfolio' ? (
              <div className="h-[600px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-white dark:bg-gray-800">
                <PortfolioVisualEditor onSave={(newJson) => {
                  setPortfolioJson(newJson);
                  setViewMode('json'); // Switch to JSON view to show the updated code
                }} />
              </div>
            ) : viewMode === 'visual' && activeTab === 'cv' ? (
              <div className="h-[600px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-white dark:bg-gray-800">
                <CVVisualEditor onSave={(newJson) => {
                  setCvJson(newJson);
                  setViewMode('json'); // Switch to JSON view to show the updated code
                }} />
              </div>
            ) : viewMode === 'visual' && activeTab === 'projects' ? (
              <div className="h-[600px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-white dark:bg-gray-800">
                <ProjectVisualEditor onSave={(newJson) => {
                  setProjectJson(newJson);
                  setViewMode('json'); // Switch to JSON view to show the updated code
                }} />
              </div>
            ) : (
              <div className="relative">
                <div className="absolute top-0 left-0 w-full h-8 bg-gray-800 rounded-t-md flex items-center px-4 border-b border-gray-700">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <span className="ml-4 text-xs text-gray-400 font-mono">
                    {activeTab === 'portfolio' ? 'src/data/PortfolioData.js' : activeTab === 'cv' ? 'src/components/CVContent.js' : 'src/components/ProjectData.js'}
                  </span>
                </div>
                
                <textarea
                  value={activeTab === 'portfolio' ? portfolioJson : activeTab === 'cv' ? cvJson : projectJson}
                  onChange={(e) => {
                    if (activeTab === 'portfolio') setPortfolioJson(e.target.value);
                    else if (activeTab === 'cv') setCvJson(e.target.value);
                    else setProjectJson(e.target.value);
                  }}
                  className="w-full h-[600px] p-4 pt-12 font-mono text-sm bg-gray-900 text-green-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  spellCheck="false"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataEditor;
